/**
 * HistoryPage — история движения средств по балансу ребёнка.
 * Маршрут: /cabinet/students/:id/history
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Stack, Group, Box, Text, Title, Paper, Button, Loader, Center, Badge, Divider,
} from '@mantine/core';
import {
  IconArrowLeft, IconArrowUpRight, IconArrowDownLeft,
  IconArrowBackUp, IconAdjustments, IconWallet,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import CabinetLayout from '../components/CabinetLayout';
import { cabinetService } from '../services/api';
import tokens from '../theme/designTokens';

const formatRub = (val) => {
  const num = Number(val);
  if (Number.isNaN(num)) return '—';
  return num.toLocaleString('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  });
};

const formatDate = (iso) => new Date(iso).toLocaleString('ru-RU', {
  day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit',
  timeZone: 'Europe/Moscow',
});

const TXN_CONFIG = {
  topup: {
    label: 'Пополнение',
    color: tokens.colors.brand.green,
    bg: tokens.colors.bg.softGreenLight,
    icon: IconArrowUpRight,
    sign: '+',
  },
  charge: {
    label: 'Списание',
    color: tokens.colors.brand.purple,
    bg: tokens.colors.bg.softPurple,
    icon: IconArrowDownLeft,
    sign: '−',
  },
  refund: {
    label: 'Возврат',
    color: tokens.colors.brand.blue,
    bg: '#E0F2FE',
    icon: IconArrowBackUp,
    sign: '+',
  },
  adjustment: {
    label: 'Корректировка',
    color: tokens.colors.brand.orange,
    bg: tokens.colors.bg.softOrange,
    icon: IconAdjustments,
    sign: '',
  },
};

const HistoryPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [studentsData, balanceData, txnData] = await Promise.all([
        cabinetService.getMyStudents(),
        cabinetService.getBalance(id),
        cabinetService.getTransactions(id, 100),
      ]);
      const items = studentsData.items || studentsData;
      const found = items.find((s) => s.id === Number(id));
      setStudent(found);
      setBalance(balanceData.balance);
      setTransactions(txnData);
    } catch (err) {
      notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <CabinetLayout>
        <Center py={80}><Loader color="brand-purple" /></Center>
      </CabinetLayout>
    );
  }

  const balanceNum = balance != null ? Number(balance) : 0;
  const hasDebt = balanceNum < 0;

  return (
    <CabinetLayout>
      <Stack gap="lg">
        <Button
          variant="subtle"
          color="brand-purple"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate('/')}
          style={{ alignSelf: 'flex-start' }}
        >
          Назад
        </Button>

        {/* Заголовок + баланс */}
        <Paper p="xl" radius="xl" style={{ background: 'white' }}>
          <Group justify="space-between" align="flex-start">
            <Box>
              <Text size="xs" fw={800} tt="uppercase" c={tokens.colors.text.muted}>
                История операций
              </Text>
              <Title
                order={2}
                style={{
                  fontFamily: tokens.typography.fontFamilyHeading,
                  color: tokens.colors.text.primary,
                  marginTop: 4,
                }}
              >
                {student && [student.first_name, student.last_name].filter(Boolean).join(' ')}
              </Title>
            </Box>
            <Box style={{ textAlign: 'right' }}>
              <Text size="xs" fw={800} tt="uppercase" c={tokens.colors.text.muted}>
                Баланс
              </Text>
              <Text
                style={{
                  fontFamily: tokens.typography.fontFamilyHeading,
                  fontWeight: 1000,
                  fontSize: 32,
                  color: hasDebt ? tokens.colors.brand.pink : tokens.colors.brand.purple,
                  lineHeight: 1.1,
                }}
              >
                {formatRub(balance)}
              </Text>
            </Box>
          </Group>
        </Paper>

        {/* Список транзакций */}
        {transactions.length === 0 ? (
          <Paper p="xl" radius="xl" style={{ background: 'white', textAlign: 'center' }}>
            <IconWallet size={48} color={tokens.colors.text.muted} stroke={1.5} />
            <Text c={tokens.colors.text.muted} fw={700} mt="sm">
              Операций пока нет
            </Text>
          </Paper>
        ) : (
          <Paper p="lg" radius="xl" style={{ background: 'white' }}>
            <Stack gap={0}>
              {transactions.map((txn, idx) => {
                const cfg = TXN_CONFIG[txn.transaction_type] || TXN_CONFIG.charge;
                const Icon = cfg.icon;
                return (
                  <React.Fragment key={txn.id}>
                    {idx > 0 && <Divider color="rgba(124,58,237,0.06)" />}
                    <Group gap="md" align="center" py="md">
                      <Box
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 14,
                          background: cfg.bg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: cfg.color,
                          flexShrink: 0,
                        }}
                      >
                        <Icon size={22} stroke={2.5} />
                      </Box>
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text
                          fw={900}
                          style={{
                            color: tokens.colors.text.primary,
                            fontFamily: tokens.typography.fontFamilyHeading,
                          }}
                        >
                          {txn.description || cfg.label}
                        </Text>
                        <Group gap="xs" mt={2}>
                          <Badge size="xs" color="gray" variant="light" radius="xl">
                            {cfg.label}
                          </Badge>
                          <Text size="xs" c={tokens.colors.text.muted} fw={700}>
                            {formatDate(txn.created_at)}
                          </Text>
                        </Group>
                      </Box>
                      <Box style={{ textAlign: 'right' }}>
                        <Text
                          fw={1000}
                          style={{
                            fontFamily: tokens.typography.fontFamilyHeading,
                            color: cfg.color,
                            fontSize: 18,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {cfg.sign}{formatRub(txn.amount).replace('−', '')}
                        </Text>
                        <Text size="xs" c={tokens.colors.text.muted} fw={700}>
                          → {formatRub(txn.balance_after)}
                        </Text>
                      </Box>
                    </Group>
                  </React.Fragment>
                );
              })}
            </Stack>
          </Paper>
        )}
      </Stack>
    </CabinetLayout>
  );
};

export default HistoryPage;
